import { useRef, useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { ScheduleGrid } from '@/components/ScheduleGrid/ScheduleGrid';
import {
  CanceledSessionsPopover,
  type CanceledSessionsPopoverHandle,
} from '@/components/ScheduleGrid/CanceledSessionsPopover';
import { CellCommentsTableProvider } from '@/features/project-cell-comments/CellCommentsTableContext';
import { useSessionCommentSummariesForRange } from '@/hooks/useSessionCommentSummariesForRange';
import { SessionModal } from '@/components/SessionDialog/SessionModal';
import { getDateRangeForMonth } from '@/schedule/scheduleUtils';
import type { Project } from '@/api/client';
import { useScheduleLogic } from '@/hooks/useScheduleLogic';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { useMiddleButtonPanScroll } from '@/hooks/useMiddleButtonPanScroll';
import { useScheduleDragDropSupport } from '@/hooks/useScheduleDragDropSupport';
import { useScheduleSearch } from '@/contexts/ScheduleSearchContext';
import { sessionMatchesScheduleSearchWords } from '@/schedule/sessionScheduleSearch';
import type { ScheduleSearchFilterState } from '@/schedule/scheduleSearchFilter';
import { parseSearchHighlightWords } from '@/utils/highlightSearchTerms';
import { PROJECTS_FOR_SCHEDULE_FINAL_QUERY_KEY } from '@/lib/projectListQueryCache';
import './SchedulePage.css';

const SCHEDULE_SEARCH_FILTER_OFF: ScheduleSearchFilterState = {
  active: false,
  words: [],
  matchIds: null,
};

type CanceledSessionsAnchor = { dateIso: string; anchor: HTMLElement };

interface SchedulePageProps {
  year: number;
  month: number;
}

export function SchedulePage({ year, month }: SchedulePageProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollReady, setScrollReady] = useState(false);
  const [canceledPopover, setCanceledPopover] = useState<CanceledSessionsAnchor | null>(null);
  const canceledPopoverAnchorRef = useRef<CanceledSessionsAnchor | null>(null);
  canceledPopoverAnchorRef.current = canceledPopover;
  const canceledSessionsPopoverRef = useRef<CanceledSessionsPopoverHandle | null>(null);

  const setScrollRef = useCallback((el: HTMLDivElement | null) => {
    (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    setScrollReady(!!el);
  }, []);

  const { from, to } = useMemo(
    () => getDateRangeForMonth(year, month),
    [year, month]
  );

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['sessions', from, to],
    queryFn: () => api.sessions.list({ from, to }),
    staleTime: 30_000,
  });

  const { data: soundengineersData } = useQuery({
    queryKey: ['soundengineers', 'all'],
    queryFn: () => api.soundengineers.list(undefined, { limit: 100, offset: 0 }),
    staleTime: 60_000,
  });
  const soundengineers = useMemo(
    () => (soundengineersData?.items ?? []).filter((e) => !e.dismissed),
    [soundengineersData]
  );

  const { data: rawProjectsData } = useQuery({
    queryKey: PROJECTS_FOR_SCHEDULE_FINAL_QUERY_KEY,
    queryFn: () => api.projects.list({ limit: 1000 }),
    staleTime: 5 * 60 * 1000,
  });

  const scheduleProjects = useMemo<Project[]>(() => {
    if (!rawProjectsData) return [];
    return Array.isArray(rawProjectsData)
      ? rawProjectsData
      : (rawProjectsData as { items: Project[] }).items ?? [];
  }, [rawProjectsData]);

  const { debouncedSearch } = useScheduleSearch();
  const scheduleSearchWords = useMemo(
    () => parseSearchHighlightWords(debouncedSearch),
    [debouncedSearch]
  );
  const scheduleSearchFilter = useMemo(() => {
    if (scheduleSearchWords.length === 0) {
      return SCHEDULE_SEARCH_FILTER_OFF;
    }
    const matchIds = new Set<string>();
    for (const s of sessions) {
      if (sessionMatchesScheduleSearchWords(s, scheduleSearchWords)) matchIds.add(s.id);
    }
    return { active: true, words: scheduleSearchWords, matchIds };
  }, [sessions, scheduleSearchWords]);

  useScheduleDragDropSupport();

  useScrollRestoration(scrollRef, scrollReady, {
    storageKey: 'schedule-scroll',
    contentReady: !isLoading,
    resetOnMonthChange: { year, month },
  });
  useMiddleButtonPanScroll(scrollRef, scrollReady);

  const logic = useScheduleLogic({ from, to });

  /** Монтирование только при открытии — enter; повторный клик по той же кнопке — requestClose (exit). */
  const openCanceledSessionsPopover = useCallback((dateIso: string, anchorEl: HTMLElement) => {
    const cur = canceledPopoverAnchorRef.current;
    if (cur && cur.dateIso === dateIso && cur.anchor === anchorEl) {
      canceledSessionsPopoverRef.current?.requestClose();
      return;
    }
    setCanceledPopover({ dateIso, anchor: anchorEl });
  }, []);

  /** Стабильные хендлеры — никогда не меняются между рендерами.
   * Обновляются только при изменении самих хендлеров или soundengineers */
  const handlersValue = useMemo(
    () => ({
      soundengineers,
      onSlotClick: logic.handleSlotClick,
      onSlotDrop: logic.handleSlotDrop,
      onSessionDropOnSession: logic.handleSessionDropOnSession,
      onAddClick: logic.openDialogForNew,
      onSessionEdit: logic.openDialogForEdit,
      onSessionClick: logic.closeDialog,
      onSessionDelete: logic.handleDeleteSession,
      onSessionResize: logic.handleResizeSession,
      onSessionUpdate: logic.handleSessionUpdate,
      onSessionOptimisticUpdate: logic.optimisticSessionUpdate,
      onRestoreCanceledSession: logic.restoreCanceledSession,
      onOpenCanceledSessionsPopover: openCanceledSessionsPopover,
    }),
    [
      soundengineers,
      logic.handleSlotClick,
      logic.handleSlotDrop,
      logic.handleSessionDropOnSession,
      logic.openDialogForNew,
      logic.openDialogForEdit,
      logic.closeDialog,
      logic.handleDeleteSession,
      logic.handleResizeSession,
      logic.handleSessionUpdate,
      logic.optimisticSessionUpdate,
      logic.restoreCanceledSession,
      openCanceledSessionsPopover,
    ]
  );

  const sessionIds = useMemo(() => sessions.map((s) => s.id), [sessions]);
  const rangeKey = useMemo(() => ({ from, to }), [from, to]);
  const { bySessionId: sessionCommentCountById } = useSessionCommentSummariesForRange(
    sessionIds,
    isLoading ? null : rangeKey
  );

  /** Volatile-состояние выделения — меняется при каждом клике по слоту.
   * Отделено от handlersValue: ре-рендерятся только DayCell, а не SessionBubble */
  const selectionValue = useMemo(
    () => ({ gridSelection: logic.gridSelection }),
    [logic.gridSelection]
  );

  const selectionForDialog = logic.gridSelection
    ? {
        date: logic.gridSelection.date,
        studioIdx: logic.gridSelection.studioIdx,
        slotIdx: logic.gridSelection.slotIdx,
        endSlotIdx: logic.gridSelection.endSlotIdx ?? logic.gridSelection.slotIdx,
      }
    : null;

  return (
    <div className="schedule-page">
      <div className="schedule-page__table-wrap">
        <div ref={setScrollRef} className="schedule-page__scroll">
          {isLoading ? (
            <div className="schedule-page__loading">Загрузка...</div>
          ) : scrollReady ? (
            <CellCommentsTableProvider>
              <ScheduleGrid
                year={year}
                month={month}
                sessions={sessions}
                scrollRef={scrollRef}
                scrollReady={scrollReady}
                handlersValue={handlersValue}
                selectionValue={selectionValue}
                scheduleProjects={scheduleProjects}
                sessionCommentCountById={sessionCommentCountById}
                scheduleSearchFilter={scheduleSearchFilter}
              />
            </CellCommentsTableProvider>
          ) : null}
        </div>
      </div>
      <SessionModal
        open={logic.dialogOpen}
        onClose={logic.closeDialog}
        onUpdateSession={logic.updateMutation.mutateAsync}
        onCreateSession={logic.createMutation.mutateAsync}
        selection={selectionForDialog}
        editingSession={logic.editingSession}
      />
      {canceledPopover && (
        <CanceledSessionsPopover
          key={canceledPopover.dateIso}
          ref={canceledSessionsPopoverRef}
          open
          onClose={() => setCanceledPopover(null)}
          dateIso={canceledPopover.dateIso}
          anchorEl={canceledPopover.anchor}
          sessions={sessions}
          onRestore={logic.restoreCanceledSession}
          onDelete={logic.handleDeleteSession}
        />
      )}
    </div>
  );
}
